import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ContentTypeIcon from "./ContentTypeIcon";
import Frame from "../../../assets/images/dummyFrame.svg";
import FrameImage from "../../../assets/images/Frame.png";
import { sendMessageToParent } from "../../../utils/helper";
import { getAppName } from "../../../utils/appConfig";
import { getDisplayNameFromCode } from "../utils/helperFunctions";
import { markFeatureEntry, ZYMED_SOURCE } from "../../../utils/zymedAnalytics";

interface TpRectangleCardProps {
  isBackground?: boolean;
  isShowcontentType?: boolean;
  isLike: boolean;
  isBookmarked: boolean;
  onLike: (isLiked: boolean) => void;
  onBookmark: (isBookmarked: boolean) => void;
  data: {
    docId: any;
    contentTitle: string;
    contentType: string;
    duration: string;
    readTime: string;
    uploadThumbnailUrl: string;
    contentDescription?: string;
    previewDeepLink?: string;
    like_count: number;
    contentTypeId: any;
    publishedDate: string;
    isExternalSharable?: number;
    previewUrl?: string;
    currentTab?: string;
    source?: string;
    deepLinkCode?: string;
    consolidatedModule?: string;
  };
}

const TpRectangleCard: React.FC<TpRectangleCardProps> = ({
  isBackground = true,
  isShowcontentType = false,
  isLike,
  isBookmarked,
  onLike,
  onBookmark,
  data,
}) => {
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const location = useLocation();

  // Get current activeTab from URL if available
  const params = new URLSearchParams(location.search);
  const tab =
    document
      .querySelector(".bg-indigo-50")
      ?.textContent?.trim()
      .toLowerCase() || "latest";
  const currentTab =
    params.get("activeTab") ||
    // If we're in TatvaPediaHomeScreen but no query param,
    // we can try to get the tab from the pathname segments
    (location.pathname === "/tatvapedia" &&
    !location.search &&
    tab !== "latest"
      ? tab
      : "latest");

  const formatDuration = (duration: string) => {
    const [hours, minutes] = duration.split(":");
    const h = parseInt(hours);
    const m = parseInt(minutes);

    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    if (m) return `${m}m`;
    return "";
  };

  const getActionText = (contentType: string) => {
    switch (contentType.toLowerCase()) {
      // New format-based content types
      case "videos":
      case "video":
        return "Watch";
      case "documents":
      case "document":
        return "Read";
      case "podcasts":
      case "podcast":
        return "Listen";
      case "slides":
      case "slide":
        return "Read";
      
      // Legacy content type cases for backward compatibility
      case "clinical education":
        return "Read";
      case "guidelines & protocols":
        return "Read";
      case "medical news":
        return "Read";
      case "patient case studies":
        return "Read";
      case "patient information material":
        return "Read";
      case "procedures & surgeries":
        return "Watch";
      case "recent clinical advances":
        return "Read";
      case "webinars & panel discussions":
        return "Watch";
      case "editorials & research case studies":
        return "Read";
      case "infographics":
      case "medical slide library":
        return "Read";
      case "expert opinions":
        return "Watch";
      default:
        return "Read";
    }
  };

  const getDuration = () => {
    const timeToShow = data.duration || data.readTime;
    return timeToShow ? formatDuration(timeToShow) : "";
  };

  const handleShare = async () => {
    const shareTitle = data?.contentTitle || "Check this out!";

    // Use public preview URL if content is externally shareable, otherwise use current URL
    let shareUrl = window.location.href;

    if (data?.isExternalSharable) {
      // Create public preview URL - you'll need to implement this route
      shareUrl = `${window.location.origin}/preview/${getAppName()}/${
        data.deepLinkCode
      }/${data.docId}`;
    }

    sendMessageToParent("share", {
      title: shareTitle,
      url: shareUrl,
    });
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    }
  };

  return (
    <div
      className={`flex gap-4 p-4 rounded-[1rem] ${
        isBackground ? "shadow-sm bg-white" : ""
      }`}
      onClick={() => {
        if (window.location.pathname === "/") {
          markFeatureEntry("/content", ZYMED_SOURCE.HOME_CARD_CLICK);
        }
        navigate("/content", {
          state: {
            id: data.docId,
            date: data.publishedDate,
            previousTab: data.currentTab || currentTab,
            source:
              data.source || window.location.pathname + window.location.search,
            currentTab: data.currentTab || currentTab,
          },
        });
      }}
    >
      <div className="flex-1 text-left min-w-0">
        {isShowcontentType && (
          <h3 className="text-[#625616] truncate max-w-[10rem] text-sm">
            {getDisplayNameFromCode(data.consolidatedModule || "")}
          </h3>
        )}
        <h3 className="text-gray-800 font-semibold line-clamp-2 mb-1">
          {data?.contentTitle}
        </h3>
        <p className="text-gray-500 text-sm mt-1 mb-2">
          {data.publishedDate
            ? new Date(data.publishedDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : ""}
          {/* {getDuration() && (
            <>
              {data.publishedDate && " | "}
              {getDuration()} {getActionText(data.contentType)}
            </>
          )} */}
        </p>
        <div className="flex items-center gap-4 mt-auto">
          <button
            onClick={(event) => {
              event.stopPropagation();
              onLike(isLike);
            }}
            className={`flex items-center gap-1 text-gray-600 rounded-full bg-[#F1F1F5] px-[0.6rem] py-[0.3rem] transition-colors ${
              isLike ? "text-indigo-600" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-4 h-4 ${isLike ? "fill-current" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
              />
            </svg>
            {data?.like_count > 0 ? data.like_count : ""}
          </button>
          {data.isExternalSharable === 1 && (
            <button
              onClick={(event) => {
                event.stopPropagation();
                handleShare();
              }}
              className="flex items-center gap-1 text-gray-600 rounded-full bg-[#F1F1F5] px-[0.5rem] py-[0.5rem] hover:bg-gray-200 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
            </button>
          )}
          <button
            onClick={(event) => {
              event.stopPropagation();
              onBookmark(isBookmarked);
            }}
            className={`flex items-center gap-1 text-gray-600 rounded-full bg-[#F1F1F5] px-[0.5rem] py-[0.5rem] transition-colors ${
              isBookmarked ? "text-indigo-600 bg-indigo-50" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-4 h-4 ${isBookmarked ? "fill-current" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        </div>
      </div>
      <div className="relative flex-shrink-0 self-start">
        {hasError || !data.uploadThumbnailUrl ? (
          <div
            className={`${
              isBackground
                ? "w-[7.6875rem] h-[8.125rem]"
                : "w-[7.6875rem] h-[6.75rem]"
            } rounded-[1.5rem] flex items-start justify-center border-[#dcdafd] border-[0.5px] overflow-hidden`}
            style={{
              backgroundImage: `url(${Frame})`,
              backgroundSize: "cover",
            }}
          >
            <img
              src={FrameImage}
              alt={data.contentTitle}
              className="w-[3.39175rem] h-[3.39175rem] object-contain my-auto"
            />
          </div>
        ) : (
          <div
            className={`${
              isBackground
                ? "w-[7.6875rem] h-[8.125rem]"
                : "w-[7.6875rem] h-[6.75rem]"
            } rounded-[1.01788rem] overflow-hidden bg-[#F1F1F5]`}
          >
            <img
              src={data?.uploadThumbnailUrl}
              alt={data?.contentTitle}
              className="w-full h-full object-cover object-top aspect-[123/130]"
              onError={() => setHasError(true)}
            />
          </div>
        )}
        <button
          className={`absolute bottom-[-0.2rem] right-[-0.2rem] w-[3.3rem] h-[3.3rem] bg-white rounded-full flex items-center justify-center shadow-lg`}
        >
          <div className="w-[2.8rem] h-[2.8rem] bg-[#4B4AD5] rounded-full flex items-center justify-center">
            <ContentTypeIcon type={data?.contentType} className="white" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default TpRectangleCard;
